import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type { Option } from '@mylighthouse/prism-ember/types/components/prism/select-input';
import HotelSettingsMyotbMyotbBaseController from 'lumen/controllers/hotel/settings/myotb/myotb-base';
import type { Pmsdatasource } from 'lumen/models';
import type HotelSettingsMyotbOtherSystemSelectionRoute from 'lumen/routes/hotel/settings/myotb/other-system-selection';
import {
  OTHER_OPTION_ID,
  STAGES,
} from 'lumen/utils/constants/otb-selfservice-constants';
import type { ModelFrom } from 'lumen/utils/routes';

type OtherSystemOption = Option & { id: number; label: string };

export default class HotelSettingsMyotbOtherSystemSelectionController extends HotelSettingsMyotbMyotbBaseController {
  declare model: ModelFrom<HotelSettingsMyotbOtherSystemSelectionRoute>;

  @service declare access: Services['access'];
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];
  @service declare store: Services['store'];

  @tracked customOtherSystemName = '';
  @tracked selectedOtherSystem: OtherSystemOption | null = null;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasSelectedNoneOfTheAbove() {
    return this.selectedOtherSystem?.id === OTHER_OPTION_ID;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isContinueDisabled() {
    // Other system must be selected.
    return !this.selectedOtherSystem;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isOtherSystemPrefilled() {
    return (
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      this.otbSelfService.stage === STAGES.OTHER_SYSTEM_SELECTION_PREFILLED_PAGE
    );
  }

  get noneOfTheAboveOption(): OtherSystemOption {
    return {
      id: OTHER_OPTION_ID,
      label: this.intl.t('general.other'),
    };
  }

  get otherSystemSelectionOptions(): OtherSystemOption[] {
    return [
      ...this.model.otherSystems.map((system) => ({
        ...system,
        id: system.id,
        label: system.display_name,
      })),
      this.noneOfTheAboveOption,
    ];
  }

  @action
  async onClickContinue(): Promise<void> {
    // Set selected properties on the local self-service object
    if (this.hasSelectedNoneOfTheAbove) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      this.otbSelfService.otherSystem = null;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      this.otbSelfService.customOtherSystemName = this.customOtherSystemName;
    } else if (this.selectedOtherSystem) {
      let otherSystem = this.store.peekRecord<Pmsdatasource>(
        'pmsdatasource',
        this.selectedOtherSystem.id,
      );
      if (!otherSystem) {
        // Create a dummy pms version record so we can set the id in the update call in the otb self-service object
        otherSystem = this.store.createRecord<Pmsdatasource>('pmsdatasource', {
          // @ts-expect-error: Type 'number' is not assignable to type 'string'.
          id: this.selectedOtherSystem.id,
        });
      }
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      this.otbSelfService.otherSystem = otherSystem;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      this.otbSelfService.customOtherSystemName = null;
    }

    // Save the self-service object
    try {
      this.isLoading = true;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      await this.otbSelfService.save();

      // Update feature flags for changed in-app states
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.access.refetchFeatureFlags();

      // Redirect based on the updated otb self-service object returned by the API in the save call
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const { stage, otherSystem } = this.otbSelfService;

      if (
        // User went back from the unsupported page, selected an unsupported PMS, selected a supported other system
        // but needs to answer other system eligibility questions.
        ((stage === STAGES.PMS_UNSUPPORTED_PAGE ||
          stage === STAGES.PMS_SETUP_PAGE) &&
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          otherSystem?.eligibilityQuestions?.length) ||
        // Or user selected a supported other system but needs to answer other system eligibility questions.
        stage === STAGES.OTHER_SYSTEM_ELIGIBILITY_QUESTIONS_PAGE
      ) {
        this.router.transitionTo(
          'hotel.settings.myotb.other-system-eligibility',
        );
      } else if (stage === STAGES.PMS_UNSUPPORTED_PAGE) {
        // User selected an unsupported other system.
        this.router.transitionTo('hotel.settings.myotb.pms-unsupported');
      } else {
        // Otherwise transition to the setup page.
        this.router.transitionTo('hotel.settings.myotb.pms-setup');
      }
    } catch (error) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.alerting.showErrorAlert();
      this.errorTracking.captureException(error);
    } finally {
      this.isLoading = false;
    }
  }

  @action onClickPrevious(): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (this.otbSelfService.eligibilityQuestionsAnswers) {
      this.router.transitionTo('hotel.settings.myotb.pms-eligibility');
    } else {
      this.router.transitionTo('hotel.settings.myotb.pms-selection');
    }
  }

  @action onSelectOtherSystem(otherSystem: OtherSystemOption): void {
    this.selectedOtherSystem = otherSystem;
  }

  @action setDefaults(): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (this.otbSelfService.otherSystem) {
      // Prefill the current selected other system.
      const selectedOtherSystem = this.otherSystemSelectionOptions.find(
        (otherSystem) =>
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          otherSystem.id.toString() === this.otbSelfService.otherSystem.id,
      );

      if (selectedOtherSystem) {
        this.selectedOtherSystem = selectedOtherSystem;
      }
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    } else if (this.otbSelfService.customOtherSystemName) {
      // If the user selected 'Other' as other system, prefill that
      this.selectedOtherSystem = this.noneOfTheAboveOption;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      this.customOtherSystemName = this.otbSelfService.customOtherSystemName;
    }
  }

  @action updateCustomOtherSystemName(event: Event): void {
    this.customOtherSystemName = (event.target as HTMLInputElement).value;
  }
}
